import React, { useState } from 'react';
import {
  Box, TextField, Button, Typography,
  InputAdornment, IconButton, Alert, CircularProgress,
} from '@mui/material';
import {
  Email, Lock, Visibility, VisibilityOff,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.png';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await InvokeApi(Config.main_url + apiList.adminLogin, { email, password });
      if (data?.code === '200') {
        await login({
          token: data.token,
          role: data.role,
          adminId: data.adminId,
          department: data.department || null,
          name: data.name || '',
        });
        navigate('/dashboard');
      } else {
        setError(data?.message || 'Invalid credentials');
      }
    } catch {
      setError('Server error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: '#f8fafc',
      fontSize: 15,
      '@media (max-height: 750px)': { fontSize: 13 },
      '& fieldset': { borderColor: '#e2e8f0' },
      '&:hover fieldset': { borderColor: '#a5b4fc' },
      '&.Mui-focused fieldset': { borderColor: '#4f46e5', borderWidth: '1.5px' },
      '&.Mui-focused': { backgroundColor: '#fafbff' },
    },
    '& .MuiInputBase-input': {
      py: '10px',
      '@media (max-height: 750px)': { py: '7px' }
    },
    '& .MuiInputBase-input::placeholder': { color: '#c0c9d8', opacity: 1 },
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#eef2ff',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background blobs */}
      <Box sx={{
        position: 'absolute', borderRadius: '50%', pointerEvents: 'none',
        width: 500, height: 500, backgroundColor: '#c7d2fe',
        top: -160, left: -160, opacity: 0.5,
        '@media (max-height: 750px)': { width: 380, height: 380, top: -120, left: -120 }
      }} />
      <Box sx={{
        position: 'absolute', borderRadius: '50%', pointerEvents: 'none',
        width: 400, height: 400, backgroundColor: '#ddd6fe',
        bottom: -120, right: -120, opacity: 0.45,
        '@media (max-height: 750px)': { width: 280, height: 280, bottom: -80, right: -80 }
      }} />
      <Box sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(circle, #a5b4fc 1px, transparent 1px)',
        backgroundSize: '26px 26px',
        opacity: 0.15,
      }} />

      {/* Card */}
      <Box
        sx={{
          width: 440,
          '@media (max-width: 600px)': { width: '90%' },
          '@media (max-height: 750px)': { width: 380 },
          backgroundColor: '#fff',
          borderRadius: '24px',
          boxShadow: '0 0 0 1px rgba(99,102,241,0.09), 0 10px 40px rgba(99,102,241,0.13)',
          position: 'relative',
          zIndex: 1,
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        {/* Accent top bar */}
        <Box sx={{ height: 6, '@media (max-height: 750px)': { height: 4 }, background: 'linear-gradient(90deg,#4f46e5,#818cf8)' }} />

        <Box sx={{
          px: 5, pt: 4.5, pb: 4,
          '@media (max-height: 750px)': { px: 3.5, pt: 3, pb: 2.5 }
        }}>

          {/* Logo + school name */}
          <Box sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            mb: 3, '@media (max-height: 750px)': { mb: 1.5 }
          }}>
            <Box
              sx={{
                width: 72, height: 72,
                '@media (max-height: 750px)': { width: 56, height: 56 },
                borderRadius: '16px',
                backgroundColor: '#f5f3ff',
                border: '1.5px solid #e0e7ff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                p: '12px', mb: 1.8,
                '@media (max-height: 750px)': { p: '10px', mb: 1 },
                boxShadow: '0 4px 14px rgba(99,102,241,0.12)',
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="St Lords Logo"
                sx={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px' }}
              />
            </Box>
            <Typography sx={{
              fontSize: 19, '@media (max-height: 750px)': { fontSize: 15 },
              fontWeight: 800, color: '#1e1b4b', textAlign: 'center', lineHeight: 1.3
            }}>
              St Lords Foundation
            </Typography>
            <Typography sx={{
              fontSize: 11, '@media (max-height: 750px)': { fontSize: 10 },
              fontWeight: 600, color: '#6366f1', letterSpacing: '1.2px', textTransform: 'uppercase', mt: 0.8,
              '@media (max-height: 750px)': { mt: 0.4 }
            }}>
              Attendance Manager
            </Typography>
          </Box>

          {/* Divider */}
          <Box sx={{ height: '1px', backgroundColor: '#f1f5f9', mb: 3, '@media (max-height: 750px)': { mb: 1.5 } }} />

          {/* Heading */}
          <Typography sx={{ fontSize: 21, '@media (max-height: 750px)': { fontSize: 17 }, fontWeight: 800, color: '#1e1b4b', mb: 0.5 }}>
            Welcome back
          </Typography>
          <Typography sx={{ fontSize: 14, '@media (max-height: 750px)': { fontSize: 12 }, color: '#94a3b8', mb: 3, '@media (max-height: 750px)': { mb: 1.5 } }}>
            Sign in to your account to continue
          </Typography>

          {/* Error */}
          {error && (
            <Alert
              severity="error"
              onClose={() => setError('')}
              sx={{ mb: 2, '@media (max-height: 750px)': { mb: 1.5 }, borderRadius: '8px', fontSize: 13, '@media (max-height: 750px)': { fontSize: 11.5, py: 0 }, bgcolor: '#fff5f5', border: '1px solid #fecaca', py: '4px' }}
            >
              {error}
            </Alert>
          )}

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit}>

            <Typography sx={{ fontSize: 13, '@media (max-height: 750px)': { fontSize: 11.5 }, fontWeight: 600, color: '#374151', mb: 1, '@media (max-height: 750px)': { mb: 0.5 }, letterSpacing: 0.2 }}>
              Email Address
            </Typography>
            <TextField
              fullWidth type="email"
              placeholder="admin@stlords.edu"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required size="small"
              sx={{ ...inputSx, mb: 2.5, '@media (max-height: 750px)': { mb: 1.5 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ fontSize: 18, '@media (max-height: 750px)': { fontSize: 16 }, color: '#a5b4fc' }} />
                  </InputAdornment>
                ),
              }}
            />

            <Typography sx={{ fontSize: 13, '@media (max-height: 750px)': { fontSize: 11.5 }, fontWeight: 600, color: '#374151', mb: 1, '@media (max-height: 750px)': { mb: 0.5 }, letterSpacing: 0.2 }}>
              Password
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter your password"
              type={showPass ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required size="small"
              sx={{ ...inputSx, mb: 3.5, '@media (max-height: 750px)': { mb: 2 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ fontSize: 18, '@media (max-height: 750px)': { fontSize: 16 }, color: '#a5b4fc' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPass(!showPass)} edge="end" size="small">
                      {showPass
                        ? <VisibilityOff sx={{ fontSize: 18, '@media (max-height: 750px)': { fontSize: 16 }, color: '#a5b4fc' }} />
                        : <Visibility sx={{ fontSize: 18, '@media (max-height: 750px)': { fontSize: 16 }, color: '#a5b4fc' }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit" fullWidth disabled={loading}
              sx={{
                py: '12px',
                '@media (max-height: 750px)': { py: '8px' },
                borderRadius: '10px',
                fontWeight: 700, fontSize: 15,
                '@media (max-height: 750px)': { fontSize: 13.5 },
                textTransform: 'none',
                color: '#fff',
                background: 'linear-gradient(90deg,#4f46e5,#6366f1)',
                boxShadow: '0 4px 14px rgba(79,70,229,0.28)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: 'linear-gradient(90deg,#4338ca,#4f46e5)',
                  boxShadow: '0 6px 20px rgba(79,70,229,0.38)',
                  transform: 'translateY(-1px)',
                },
                '&:active': { transform: 'translateY(0)' },
                '&.Mui-disabled': { opacity: 0.55, color: '#fff' },
              }}
            >
              {loading ? <CircularProgress size={19} sx={{ color: '#fff' }} /> : 'Sign In'}
            </Button>
          </Box>

          {/* Footer */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 3.5, '@media (max-height: 750px)': { mt: 2 } }}>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#f1f5f9' }} />
            <Typography sx={{ fontSize: 11, '@media (max-height: 750px)': { fontSize: 10 }, color: '#cbd5e1', fontWeight: 500, whiteSpace: 'nowrap' }}>
              2026 St Lords Foundation · All rights reserved
            </Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#f1f5f9' }} />
          </Box>

        </Box>
      </Box>
    </Box>
  );
}
